import { Col, Dropdown, Input, Row, Space, Tooltip } from 'antd';
import { RevxTable, TableColumnsType } from 'components/RevxTable';
import {
  BillingItem,
  ShippingBillingProducerItem,
  WorkspaceTabShippingBillingDTO,
} from 'domains/workspaceDetailDomain';
import React, { useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import workspaceService from 'services/jobService';
import { sleep } from 'utils/misc';
import {
  DEFAULT_PLACEHOLDER,
  INPUT_NORMAL_MAX_LENGTH,
  INTERNAL_DISTRIBUTION,
  SHIPPING_WILL_CALL,
  STATIC_PAGE_URL_RESELLER,
  WORKSPACE_DELAY_TIME,
} from 'utils/constants';
import { JobItem, JobWorkflowStatusEnum } from 'domains/workspaceDomain';
import { addressTranslator } from 'utils/address';
import { RevxDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL } from 'components/RevxDate';
import { RevxMoney } from 'components/RevxMoney';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { UnderlineText } from 'components/RevxTypography';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { BillingRequestor } from 'domains/orderDomain';
import moment from 'moment';
import { dateToWeek } from 'utils/date';
import { RevxOverlay } from 'components/RevxOverlay';
import { RevxJobConfigCalendar } from '../../../../../components/RevxJobConfigCalendar';
import { CalendarIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import jobService from 'services/jobService';
import { useGetJobCountHandler } from 'hooks';
import InvoiceFiles from './InvoiceFiles';
import CostDistributionDisplay from './CostDistributionDisplay';

const { Nonstandard } = CarrierTypeEnum;

export const addressLine = (addressInfo: BillingItem | BillingRequestor) => {
  const addressLine = [
    addressInfo?.addressLine1,
    addressInfo?.addressLine2,
    addressInfo?.city,
    addressInfo?.state,
    addressInfo?.country,
    addressInfo?.postalCode,
  ];
  return addressLine.filter((item) => item).join(', ');
};

type Props = {
  jobData: JobItem;
  data?: WorkspaceTabShippingBillingDTO;
  hidePrice?: boolean;
};
const columns: TableColumnsType<ShippingBillingProducerItem> = [
  {
    title: 'Address',
    dataIndex: 'address',
    render: (v, r) => {
      return addressTranslator.dataToAddress(r.shippingInfo);
    },
  },
  {
    title: 'Quantity',
    dataIndex: 'itemQuantity',
    width: 100,
  },
  {
    title: 'Shipping Account',
    dataIndex: ['shippingInfo', 'accountType'],
    width: 200,
    render: (v, r) => {
      return v + (r.shippingInfo.account && ` - ${r.shippingInfo.account}`);
    },
  },
  {
    title: 'Shipping Method',
    dataIndex: ['shippingInfo', 'carrierService'],
    width: 200,
  },
  {
    title: 'Requested Delivery Date',
    dataIndex: 'expectedDeliveryDate',
    width: 200,
    render: (v) => {
      return <RevxDate dateStr={v} />;
    },
  },
  {
    title: 'Price',
    dataIndex: 'price',
    width: 100,
    render: (v) => {
      return <RevxMoney value={v} />;
    },
  },
];
const ShippingBillingProducer = ({ jobData, data, hidePrice }: Props) => {
  const [markingPaid, setMarkingPaid] = useState(false);
  const visibleColumns = hidePrice
    ? columns.filter((column) => column.title !== 'Price')
    : columns;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const getJobCountHandler = useGetJobCountHandler();

  const isInvoiced = jobData.workflowStatus === JobWorkflowStatusEnum.INVOICING;

  const isInProduction =
    jobData.workflowStatus === JobWorkflowStatusEnum.IN_PRODUCTION;
  if (!data?.producerShipping) return null;
  const infoRender = (title: string, data: BillingItem) => {
    return (
      <Col span={12}>
        <div className="col-container col-container-title">{title}:</div>
        <div className="col-container">{data.name}</div>
        <div className="col-container">{addressLine(data)}</div>
        <div className="col-container">
          <span className="bold">Attention: </span>
          <span>{data.attention}</span>
        </div>
        <div className="col-container">{data.email}</div>
        <div className="col-container">{data.telephone}</div>
        <div className="col-container">
          <a
            href={STATIC_PAGE_URL_RESELLER}
            target="_blank"
            style={{ textDecoration: 'underline' }}
            rel="noreferrer"
          >
            {t('workspace.list.resaleCertificate')}
          </a>
        </div>
      </Col>
    );
  };
  const {
    producerShipping: { items, readyDate },
    producerBilling: { billTo, requestor },
  } = data;

  const reloadWorkspace = async () => {
    await sleep(WORKSPACE_DELAY_TIME);
    await dispatch.app.hideLoading();
    dispatch.workspace.reloadJobList();
    getJobCountHandler();
  };

  return (
    <div>
      <InvoiceFiles jobData={jobData} data={data} />
      <div className="shipping-billing__title">
        <Space>
          <span className="bold">Ready date / ship date: </span>
          <span>
            <RevxDate dateStr={readyDate} isShippingDate={true} />
          </span>
        </Space>
      </div>
      <div className="shipping-billing__content">
        <RevxTable<ShippingBillingProducerItem>
          className="no-border"
          dataSource={items}
          columns={visibleColumns}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <div>
                <TrackingCodeNode
                  info={record}
                  jobData={jobData}
                  readyDate={readyDate}
                />
              </div>
            ),
            expandIcon: ({ expanded, onExpand, record }) => {
              if (!record.shipped && !isInProduction) return null;
              return (
                <i
                  className={`allow ${!expanded ? 'right' : ''}`}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            defaultExpandAllRows: true,
          }}
          rowKey={(record) => record.fulfillmentId}
        />
        <div className="billing-info">
          <RevxFlexLayout className="title">Billing</RevxFlexLayout>
          <div className="content">
            <Row>
              {infoRender('Bill To', billTo)}
              {infoRender('Requestor', requestor)}
            </Row>
          </div>
        </div>
        <div className="mark-as-paid-btn">
          {isInvoiced && (
            <RevxButton
              loading={markingPaid}
              onClick={async () => {
                await dispatch.app.showLoading();
                setMarkingPaid(true);
                try {
                  await jobService.supplierMarkAsPaid(jobData.id);
                } catch {}
                setMarkingPaid(false);
                await dispatch.alert.getNotificationAndAlerts();
                await reloadWorkspace();
              }}
            >
              {t('workspace.detail.supplierBilling.markAsPaid')}
            </RevxButton>
          )}
        </div>
        <CostDistributionDisplay
          data={data.costDistribution}
          hidePrice={hidePrice}
        />
      </div>
    </div>
  );
};
export default ShippingBillingProducer;

type TrackingCodeNodeProps = {
  info: ShippingBillingProducerItem;
  jobData: JobItem;
  readyDate: string;
};
const TrackingCodeNode = ({ info, jobData }: TrackingCodeNodeProps) => {
  const {
    shippingInfo: { carrierType, trackingCode },
    shipped,
    fulfillmentId,
    shipDate: initShipDate,
  } = info;
  const [code, setCode] = useState(trackingCode);
  const [shipDate, setShipDate] = useState(initShipDate);
  const [shippingDetails, setShippingDetails] = useState(info.shippingDetail);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const updateNonstandardShipStatus = async (
    item: ShippingBillingProducerItem
  ) => {
    await dispatch.app.showLoading();
    try {
      await workspaceService.updateJobShippingItemFulfillmentNonstandard(
        jobData.id,
        item.fulfillmentId,
        {
          shipDate,
          shippingDetails,
        }
      );
      await dispatch.app.hideLoading();
      await sleep(WORKSPACE_DELAY_TIME);
      await dispatch.workspace.reloadJobList();
    } catch {
      await dispatch.app.hideLoading();
    }
  };
  const nonstandardShipDateBlock = (item: ShippingBillingProducerItem) => {
    const {
      shippingInfo: { carrierService },
      shipped,
    } = item;
    const isWillCall = carrierService === SHIPPING_WILL_CALL;
    const isInternalDistribution = carrierService === INTERNAL_DISTRIBUTION;
    const notProductionAndNotShipped =
      jobData.workflowStatus !== JobWorkflowStatusEnum.IN_PRODUCTION &&
      !shipped;
    return (
      <RevxFlexLayout className="shipping-item-shipped nonstandard-ship-item">
        <div className="shipping-item-shipped__info-block">
          <div>
            {notProductionAndNotShipped ? (
              <div>
                {!shipped ? (
                  <label>{t('workspace.list.estimatedShipDate')}: </label>
                ) : (
                  <label>{t('workspace.list.dateShipped')}: </label>
                )}
                <span>{dateToWeek(item.shippingInfo.estimatedShipTime)}</span>
                <RevxDate
                  dateStr={item.shippingInfo.estimatedShipTime}
                  isShippingDate={true}
                />
              </div>
            ) : shipped ? (
              <div className="ship-date-info-block">
                {isWillCall || isInternalDistribution ? (
                  <>
                    <div>
                      <span>{t('workspace.list.datePickedUp')}:&nbsp;</span>
                      <span>
                        <RevxDate
                          dateStr={item.shipDate}
                          isShippingDate={true}
                        />
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span>{t('workspace.list.dateShipped')}:&nbsp;</span>
                      <span>
                        <RevxDate
                          dateStr={item.shipDate}
                          isShippingDate={true}
                        />
                      </span>
                    </div>
                    <div>
                      <span>{t('workspace.list.shippingDetails')}:&nbsp;</span>
                      <Tooltip title={item.shippingDetail}>
                        <span className="shipping-details-wrap">
                          {item.shippingDetail || DEFAULT_PLACEHOLDER}
                        </span>
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="nonstandard-block">
                  <RevxFlexLayout align="center" className="">
                    <p className="ship-date-item mr-10">
                      <span className="ship-date-label-color">
                        {isWillCall || isInternalDistribution
                          ? 'Ready Date'
                          : 'Ship Date'}
                      </span>
                      : <RevxDate dateStr={shipDate} isShippingDate={true} />
                    </p>
                    <Dropdown
                      trigger={['click']}
                      overlay={
                        <RevxOverlay style={{ padding: 20 }}>
                          <RevxJobConfigCalendar
                            color="green"
                            defaultValue={moment(item.shipDate)}
                            onChange={(date) => {
                              setShipDate(
                                date.format(RFQ_SHIPPING_DATE_FORMAT_LOCAL)
                              );
                            }}
                          />
                        </RevxOverlay>
                      }
                    >
                      <p>
                        <CalendarIcon className="cal" />
                      </p>
                    </Dropdown>
                  </RevxFlexLayout>
                  {isWillCall || isInternalDistribution ? (
                    <RevxButton
                      className="ready-pickup-btn"
                      type="primary"
                      onClick={() => updateNonstandardShipStatus(item)}
                    >
                      {t('workspace.detail.supplierShipping.readyForPickup')}
                    </RevxButton>
                  ) : (
                    <RevxFlexLayout className="shipment-input">
                      <Input
                        placeholder="Shipment details here"
                        maxLength={INPUT_NORMAL_MAX_LENGTH}
                        value={shippingDetails}
                        onChange={(v) => {
                          setShippingDetails(v.target.value);
                        }}
                      />
                      <RevxButton
                        className="submit-btn ml-20"
                        type="primary"
                        onClick={async () => {
                          await updateNonstandardShipStatus(item);
                        }}
                      >
                        Submit
                      </RevxButton>
                    </RevxFlexLayout>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </RevxFlexLayout>
    );
  };

  const isNonstandard = carrierType === Nonstandard;
  return (
    <div className="tracking-code">
      {!isNonstandard ? (
        <>
          {((shipped && trackingCode) || !shipped) && (
            <div>
              {shipped && (
                <div className="mb-10">
                  <label>{t('workspace.list.dateShipped')}: </label>
                  <span>
                    <RevxDate dateStr={shipDate} isShippingDate={true} />
                  </span>
                </div>
              )}
              <div className="tracking-info">
                <RevxTrackingInfo
                  carrierType={carrierType}
                  value={code}
                  defaultEmptyEditing
                  editable
                  onChange={async (value) => {
                    try {
                      await dispatch.app.showLoading();
                      await workspaceService.updateJobShippingItemFulfillment(
                        jobData.id,
                        fulfillmentId,
                        {
                          trackingCode: value,
                        }
                      );
                      setCode(value);
                      await sleep(WORKSPACE_DELAY_TIME);
                      await dispatch.workspace.reloadJobList();
                    } catch {
                      setCode('');
                    } finally {
                      await dispatch.app.hideLoading();
                    }
                  }}
                />
              </div>
              {!shipped && !trackingCode && !code && (
                <UnderlineText
                  color="blue"
                  className="skip-tracking-btn"
                  onClick={async () => {
                    await dispatch.app.showLoading();
                    await workspaceService.skipForTrackNumber(
                      jobData.id,
                      fulfillmentId
                    );
                    await sleep(WORKSPACE_DELAY_TIME);
                    await dispatch.app.hideLoading();
                    await dispatch.workspace.reloadJobList();
                  }}
                >
                  {t('workspace.detail.shippingBilling.shipWithoutTracking')}
                </UnderlineText>
              )}
            </div>
          )}
          {shipped && !trackingCode && (
            <div className="ship-date-info-block">
              <label>{t('workspace.list.dateShipped')}: </label>
              <span>{dateToWeek(shipDate)}, </span>
              <RevxDate dateStr={shipDate} isShippingDate={true} />
            </div>
          )}
        </>
      ) : (
        <div>{nonstandardShipDateBlock(info)}</div>
      )}
    </div>
  );
};
